/**
 * RegisterPaymentModal — TES-03: Registrar pago de operación de factoring
 */
import { todayISO } from '../../../lib/utils'
import { useState } from 'react'
import DatePicker from '@/components/DatePicker'
import { Loader2 } from 'lucide-react'
import { useRegisterFactoringPayment } from '../hooks/useTreasury'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { Modal } from '@/components/ds/Modal'

interface Props {
  operationId: string
  totalCeded: number
  onClose: () => void
}

export function RegisterPaymentModal({ operationId, totalCeded, onClose }: Props) {
  const register = useRegisterFactoringPayment()
  const { data: accountsData } = useAccounts({ is_imputable: 'true', is_active: 'true' })
  const accounts = accountsData?.accounts?.filter(a => a.account_type === 'asset') ?? []

  const today = todayISO()

  const [form, setForm] = useState({
    amount_received_net: '',
    commissions_charged: '0',
    payment_date: today,
    bank_account: '',
    notes: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const set = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }))

  // Al ingresar el neto recibido, prellenar la comisión como (total cedido −
  // neto): el backend exige neto + comisión = total cedido. El usuario puede
  // ajustarla si el banco cobró distinto.
  const setNet = (value: string) => {
    const net = parseFloat(value)
    setForm(f => ({
      ...f,
      amount_received_net: value,
      commissions_charged:
        !isNaN(net) && totalCeded > 0
          ? String(Math.max(0, Math.round(totalCeded - net)))
          : f.commissions_charged,
    }))
  }

  const fmt = (n: number) => n.toLocaleString('es-CL', { maximumFractionDigits: 0 })
  const net = parseFloat(form.amount_received_net) || 0
  const comm = parseFloat(form.commissions_charged) || 0
  const sumOk = totalCeded > 0 && Math.abs(net + comm - totalCeded) < 1

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    try {
      await register.mutateAsync({ id: operationId, ...form })
      onClose()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { details?: Record<string, string[]>; message?: string } } } }
      const details = e.response?.data?.error?.details
      const message = e.response?.data?.error?.message
      if (details) {
        const mapped: Record<string, string> = {}
        Object.entries(details).forEach(([k, v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v) })
        setErrors(mapped)
      } else if (message) {
        setErrors({ _: message })
      }
    }
  }

  return (
    <Modal open onClose={onClose} title="Registrar Pago de Factoring" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {errors._ && (
          <div className="p-3 bg-danger-surface border border-danger-border rounded-lg text-xs text-danger-content-soft">{errors._}</div>
        )}
        <div>
          <label htmlFor="registerpaymentmodal-bank-account" className="block text-xs font-medium text-content-muted mb-1">Cuenta Bancaria *</label>
          <select id="registerpaymentmodal-bank-account"
            value={form.bank_account}
            onChange={e => set('bank_account', e.target.value)}
            required
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          >
            <option value="">Seleccionar cuenta...</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
            ))}
          </select>
          {errors.bank_account && <p className="text-xs text-danger-solid mt-1">{errors.bank_account}</p>}
        </div>
        <div>
          <label htmlFor="registerpaymentmodal-payment-date" className="block text-xs font-medium text-content-muted mb-1">Fecha de Pago *</label>
          <DatePicker id="registerpaymentmodal-payment-date" value={form.payment_date} onChange={v => set('payment_date', v)} required />
        </div>
        {totalCeded > 0 && (
          <div className="flex items-center justify-between text-xs bg-soft rounded-lg px-3 py-2">
            <span className="text-content-muted">Total cedido</span>
            <span className="font-medium text-content">{fmt(totalCeded)}</span>
          </div>
        )}
        <div>
          <label htmlFor="registerpaymentmodal-amount-received-net" className="block text-xs font-medium text-content-muted mb-1">Monto Neto Recibido *</label>
          <input id="registerpaymentmodal-amount-received-net"
            type="number"
            min="0"
            step="1"
            value={form.amount_received_net}
            onChange={e => setNet(e.target.value)}
            required
            placeholder="0.00"
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />
          {errors.amount_received_net && <p className="text-xs text-danger-solid mt-1">{errors.amount_received_net}</p>}
        </div>
        <div>
          <label htmlFor="registerpaymentmodal-commissions-charged" className="block text-xs font-medium text-content-muted mb-1">Comisiones Cobradas</label>
          <input id="registerpaymentmodal-commissions-charged"
            type="number"
            min="0"
            step="1"
            value={form.commissions_charged}
            onChange={e => set('commissions_charged', e.target.value)}
            placeholder="0.00"
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
          />
          {totalCeded > 0 && (
            <p className={`text-[11px] mt-1 ${sumOk ? 'text-content-subtle' : 'text-warning-content-soft'}`}>
              {sumOk
                ? 'Se calcula como total cedido − neto recibido. Ajústala si el banco cobró distinto.'
                : `Neto + comisión (${fmt(net + comm)}) debe igualar el total cedido (${fmt(totalCeded)}).`}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="registerpaymentmodal-notes" className="block text-xs font-medium text-content-muted mb-1">Notas</label>
          <textarea id="registerpaymentmodal-notes"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            rows={2}
            className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary resize-none"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 border border-divider rounded-lg text-sm text-content-muted hover:bg-soft">
            Cancelar
          </button>
          <button type="submit" disabled={register.isPending}
            className="flex-1 px-4 py-2 bg-success-fill text-white rounded-lg text-sm hover:bg-success-fill-hover disabled:opacity-50 flex items-center justify-center gap-2">
            {register.isPending && <Loader2 size={13} className="animate-spin" />}
            Registrar Pago
          </button>
        </div>
      </form>
    </Modal>
  )
}
